import {ELOTypeEnum, LobbyImage} from '@colonist/types'
import {createDivIn, createExternalImgIn, createHeaderIn, createImgIn, createParagraphIn, setElementInnerText, setElementOnClick} from '../../ui_utils/DomUtils'
import {TranslationObject} from '@colonist/i18n'
import {BadgeData, UIProgressBadgeElement} from './UIProgressBadgeElement'
import {ProgressBarData, UIProgressBarElement} from './UILockedProgressBarElement'
import IconPlayTime from '@static/images/icon_playtime.svg'
import IconPlayerCount from '@static/images/icon_player_count.svg'
import IconBotCount from '@static/images/icon_bot_count.svg'
import {BotsMatchType, isBotMatchType, MatchmakingMatchTypes, MatchType} from '@colonist/game-base-types'
import {FindGameRankedState} from '../../../../../shared/ranked/ranked_user_state_data/FindGameRankedState'
import {
    getMatchTypeDetails,
    getMatchTypeEligibilityAndCreatePopup,
    getGameModeStringForAnalytics,
    trackMatchmakingTimeToQueueStart,
    RankedMatchTypeData,
    getMatchTypePlayTime,
    getClientCKRankedState,
    getMatchTypePlayerAndBotCount,
    updateMatchmakingPlayButtonState,
    isMatchTypeCasualLocked,
    getCasualRedirectMatchType,
} from '../../matchmaking/MatchmakingUtilsClient'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {StorageWrapper} from '../../StorageWrapper'
import {ClientUserState} from '../../ClientUserState'
import {TimeUtils} from '@colonist/utils'
import {createShuffleQueueIconStack} from '../../matchmaking/ShuffleQueueUtils'
import {RankedConstants} from '../../../../../shared/GameEnums'
import {ClientShuffleQueueController} from '../../shuffle/ClientShuffleQueueController'
import {ViewPopupFreePlayDiceRoll} from '../../ui_popup/ViewPopupFreePlayDiceRoll'
import {CKRankedState} from '../../../../../shared/matchmaking/MatchmakingUtils'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'

export enum MatchmakingCardCategory {
    Bots,
    Casual,
    Ranked,
}

interface MatchmakingCard {
    category: MatchmakingCardCategory
    icon: LobbyImage
    title: TranslationObject
    subtitle?: TranslationObject
    description?: TranslationObject
    showDescriptionLockIcon?: boolean | undefined
    seasonNote?: TranslationObject
    time: number | undefined
    playerCount: number
    botCount: number
    nextRotationISO?: string | undefined
    matchType: MatchType
}

type StartGameCallback = (matchType: MatchType) => void
type ModeDetailsCallback = (matchType: MatchType) => void

interface ModeCard {
    element: HTMLDivElement
    data: MatchmakingCard
    badge?: UIProgressBadgeElement
    progressBar?: UIProgressBarElement
    infoIcon: HTMLDivElement
}

export class UIModeSelectorController {
    private readonly cardsContainer: HTMLDivElement
    private readonly playButton: HTMLButtonElement
    private readonly botsTab: HTMLDivElement
    private readonly casualTab: HTMLDivElement
    private readonly rankedTab: HTMLDivElement
    private readonly modeSelectorAd: HTMLDivElement | null

    private currentTab: MatchmakingCardCategory | undefined
    private readonly cards: ModeCard[] = []
    private selectedCard: ModeCard | undefined
    private readonly onStartGame: StartGameCallback
    private readonly onShowDetails: ModeDetailsCallback

    private componentShownTimestamp: number
    private hasTrackedFirstQueueStart: boolean = false

    rankedStates: FindGameRankedState[] = []
    private countdownInterval: NodeJS.Timeout | undefined
    private cnKCountdownEndTimestamp: number | undefined
    private cnKProgressText: TranslationObject | undefined

    readonly waitUntilInitialized: Promise<void>
    private resolveInitialized: () => void

    constructor(initTimestamp: number, onStartGame: StartGameCallback, onShowDetails: ModeDetailsCallback) {
        this.onStartGame = onStartGame
        this.onShowDetails = onShowDetails

        this.waitUntilInitialized = new Promise((resolve) => {
            this.resolveInitialized = resolve
        })

        this.cardsContainer = document.getElementById('mm-mode-cards-container') as HTMLDivElement
        this.playButton = document.getElementById('mm-mode-card-button') as HTMLButtonElement
        this.botsTab = document.getElementById('mm-mode-tab-bots') as HTMLDivElement
        this.casualTab = document.getElementById('mm-mode-tab-casual') as HTMLDivElement
        this.rankedTab = document.getElementById('mm-mode-tab-ranked') as HTMLDivElement
        this.modeSelectorAd = document.getElementById('mm-mode-selector-ad') as HTMLDivElement | null

        this.componentShownTimestamp = initTimestamp

        this.initTabClickHandlers()
        this.setupPlayButtonHandler()
        void this.initCardsAndBadges()

        ClientUserState.registerChangeListener(() => {
            this.resetRankedCards()
        })

        ClientShuffleQueueController.registerChangeListener(() => {
            UIModeSelectorController.addNewBadgeIfNeeded(this.casualTab, true)
            this.updateShuffleCard()
        })
    }

    onHomepageShow() {
        this.componentShownTimestamp = Date.now()
        this.startCountdownTimer()
    }

    selectTab(tab: MatchmakingCardCategory) {
        this.handleTabClick(tab)
    }

    selectCardByMatchType(matchType: MatchType) {
        const card = this.cards.find((c) => c.data.matchType == matchType)
        if(card == undefined) return
        if(card.data.category != this.currentTab) {
            this.handleTabClick(card.data.category)
        }
        this.selectCard(card)
    }

    private async initCardsAndBadges() {
        this.createBotCards()
        await ClientUserState.waitUntilFresh
        UIModeSelectorController.addNewBadgeIfNeeded(this.casualTab)
        this.createCasualCards()
        await this.createRankedCards()
        this.setCardsView()
        this.selectLastPlayedMatchmakingMode()
        this.startCountdownTimer()
        this.updateModeSelectorAdVisibility()
        this.resolveInitialized()
    }

    private updateModeSelectorAdVisibility() {
        if(this.modeSelectorAd == null) return

        this.modeSelectorAd.style.display = ''
        const isCardsScrolling = this.cardsContainer.scrollHeight > this.cardsContainer.clientHeight
        if(isCardsScrolling) this.modeSelectorAd.style.display = 'none'
    }

    private initTabClickHandlers() {
        setElementOnClick(this.botsTab, () => {
            this.handleTabClick(MatchmakingCardCategory.Bots)
        })

        setElementOnClick(this.casualTab, () => {
            this.handleTabClick(MatchmakingCardCategory.Casual)
        })

        setElementOnClick(this.rankedTab, () => {
            this.handleTabClick(MatchmakingCardCategory.Ranked)
        })
    }

    private setupPlayButtonHandler() {
        setElementOnClick(this.playButton, () => {
            this.handlePlayButtonClick()
        })
    }

    private static addNewBadgeIfNeeded(parent: HTMLDivElement, applyBadgeClass: boolean = false) {
        const isLoggedIn = ClientUserState.userState?.isLoggedIn
        const shuffleGamesPlayed = ClientShuffleQueueController.shufflePlayerData?.totalGameCount

        const shouldHideBadge = isLoggedIn && shuffleGamesPlayed != 0
        if(shouldHideBadge) return

        const existingBadge = parent.querySelector('.badge-red')
        if(existingBadge != null) return

        const isMobile = UIUtilsEjsVariables.isUserOnMobileAppOrWeb
        const badge = createDivIn(parent, isMobile ? 'badge-red badge-sm' : 'badge-red badge-md')
        if(applyBadgeClass) badge.classList.add('badge')
        createParagraphIn(badge, {key: 'strings:homePage.matchmakingSelect.newBadge'}, '')
    }

    private createBotCards() {
        const beginnerCounts = getMatchTypePlayerAndBotCount(BotsMatchType.BeginnerMode)
        const easyCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Easy)
        const mediumCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Medium)
        const hardCounts = getMatchTypePlayerAndBotCount(BotsMatchType.Hard)

        const cards: MatchmakingCard[] = [
            {
                icon: LobbyImage.BotBeginner,
                category: MatchmakingCardCategory.Bots,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.beginner'},
                time: getMatchTypePlayTime(BotsMatchType.BeginnerMode),
                playerCount: beginnerCounts.playerCount,
                botCount: beginnerCounts.botCount,
                matchType: BotsMatchType.BeginnerMode,
            },
            {
                icon: LobbyImage.BotEasy,
                category: MatchmakingCardCategory.Bots,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.easy'},
                time: getMatchTypePlayTime(BotsMatchType.Easy),
                playerCount: easyCounts.playerCount,
                botCount: easyCounts.botCount,
                matchType: BotsMatchType.Easy,
            },
            {
                icon: LobbyImage.BotMedium,
                category: MatchmakingCardCategory.Bots,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.medium'},
                time: getMatchTypePlayTime(BotsMatchType.Medium),
                playerCount: mediumCounts.playerCount,
                botCount: mediumCounts.botCount,
                matchType: BotsMatchType.Medium,
            },
            {
                icon: LobbyImage.BotHard,
                category: MatchmakingCardCategory.Bots,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.hard'},
                time: getMatchTypePlayTime(BotsMatchType.Hard),
                playerCount: hardCounts.playerCount,
                botCount: hardCounts.botCount,
                matchType: BotsMatchType.Hard,
            },
        ]

        for(const card of cards) {
            this.generateCard(card)
        }
    }

    private createCasualCards() {
        const shuffleDetails = getMatchTypeDetails(MatchmakingMatchTypes.UnrankedShuffle)

        const shuffleCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedShuffle)
        const casual1v1Counts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedClassic1v1)
        const casual4PlayerCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedClassic4P)

        const cards: {card: MatchmakingCard, badge?: BadgeData, progressBar?: ProgressBarData}[] = [
            {
                card: {
                    icon: LobbyImage.GameMode1v1,
                    category: MatchmakingCardCategory.Casual,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic1v1),
                    playerCount: casual1v1Counts.playerCount,
                    botCount: casual1v1Counts.botCount,
                    matchType: MatchmakingMatchTypes.UnrankedClassic1v1,
                },
            },
            {
                card: {
                    icon: LobbyImage.GameModeBase,
                    category: MatchmakingCardCategory.Casual,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic4P),
                    playerCount: casual4PlayerCounts.playerCount,
                    botCount: casual4PlayerCounts.botCount,
                    matchType: MatchmakingMatchTypes.UnrankedClassic4P,
                },
            },
            {
                card: {
                    icon: LobbyImage.GameModeShuffle,
                    category: MatchmakingCardCategory.Casual,
                    title: {key: 'strings:homePage.unranked.shuffle'},
                    ...(shuffleDetails.subtitle != undefined && {subtitle: shuffleDetails.subtitle}),
                    ...(shuffleDetails.description != undefined && {description: shuffleDetails.description}),
                    ...(shuffleDetails.showDescriptionLockIcon && {showDescriptionLockIcon: shuffleDetails.showDescriptionLockIcon}),
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedShuffle),
                    playerCount: shuffleCounts.playerCount,
                    botCount: shuffleCounts.botCount,
                    nextRotationISO: ClientShuffleQueueController.shuffleQueueData?.endDate,
                    matchType: MatchmakingMatchTypes.UnrankedShuffle,
                },
                ...(shuffleDetails.badge && {badge: shuffleDetails.badge}),
                ...(shuffleDetails.progressBar && {progressBar: shuffleDetails.progressBar}),
            },
        ]

        for(const {card, badge, progressBar} of cards) {
            this.generateCard(card, badge, progressBar)
        }

        const hasPlayedEnoughGames = (ClientUserState.userState?.totalCompletedGameCount ?? 0) >= RankedConstants.NumberOfGamesToUnlockRanked
        const shuffleCard = this.cards.find((c) => c.data.matchType == MatchmakingMatchTypes.UnrankedShuffle)
        if(shuffleCard == undefined) return

        const shuffleQueueData = ClientShuffleQueueController.shuffleQueueData
        if(shuffleQueueData && hasPlayedEnoughGames) {
            const iconElement = shuffleCard.element.querySelector('.mm-mode-card-icon')
            if(iconElement == undefined) return

            const stackedIcon = createShuffleQueueIconStack(shuffleQueueData.mapId, shuffleQueueData.modeId)
            if(stackedIcon == undefined) return

            stackedIcon.classList.add('mm-mode-card-icon')
            iconElement.replaceWith(stackedIcon)
        }

        UIModeSelectorController.addNewBadgeIfNeeded(shuffleCard.element)
    }

    private async createRankedCards() {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiFindGameState(), undefined, undefined, false)
        if(response == undefined) return
        if(!response.isSuccess) return
        this.rankedStates = response.json as FindGameRankedState[]

        const ranked4PData = getMatchTypeDetails(MatchmakingMatchTypes.RankedClassic4P, this.rankedStates)
        const ranked1v1Data = getMatchTypeDetails(MatchmakingMatchTypes.RankedClassic1v1, this.rankedStates)
        const rankedCnKData = getMatchTypeDetails(MatchmakingMatchTypes.RankedCitiesAndKnights4P, this.rankedStates)
        const rankedRush4PData = getMatchTypeDetails(MatchmakingMatchTypes.RankedRush4P, this.rankedStates)

        const ranked1v1Counts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedClassic1v1)
        const ranked4PCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedClassic4P)
        const rankedCnKCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedCitiesAndKnights4P)
        const rankedRush4PCounts = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.RankedRush4P)

        const cards: {card: MatchmakingCard, badge?: BadgeData, progressBar?: ProgressBarData}[] = [
            {
                card: {
                    icon: LobbyImage.GameModeRanked1v1,
                    category: MatchmakingCardCategory.Ranked,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                    ...(ranked1v1Data.description && {description: ranked1v1Data.description}),
                    ...(ranked1v1Data.showDescriptionLockIcon && {showDescriptionLockIcon: true}),
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic1v1),
                    playerCount: ranked1v1Counts.playerCount,
                    botCount: ranked1v1Counts.botCount,
                    matchType: MatchmakingMatchTypes.RankedClassic1v1,
                },
                ...(ranked1v1Data.badge && {badge: ranked1v1Data.badge}),
                ...(ranked1v1Data.progressBar && {progressBar: ranked1v1Data.progressBar}),
            },
            {
                card: {
                    icon: LobbyImage.GameModeRanked4P,
                    category: MatchmakingCardCategory.Ranked,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                    ...(ranked4PData.description && {description: ranked4PData.description}),
                    ...(ranked4PData.showDescriptionLockIcon && {showDescriptionLockIcon: true}),
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic4P),
                    playerCount: ranked4PCounts.playerCount,
                    botCount: ranked4PCounts.botCount,
                    matchType: MatchmakingMatchTypes.RankedClassic4P,
                },
                ...(ranked4PData.badge && {badge: ranked4PData.badge}),
                ...(ranked4PData.progressBar && {progressBar: ranked4PData.progressBar}),
            },
            {
                card: {
                    icon: LobbyImage.GameModeCnK,
                    category: MatchmakingCardCategory.Ranked,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.citiesAndKnights'},
                    ...(rankedCnKData.description && {description: rankedCnKData.description}),
                    ...(rankedCnKData.showDescriptionLockIcon && {showDescriptionLockIcon: true}),
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedCitiesAndKnights4P),
                    playerCount: rankedCnKCounts.playerCount,
                    botCount: rankedCnKCounts.botCount,
                    matchType: MatchmakingMatchTypes.RankedCitiesAndKnights4P,
                },
                ...(rankedCnKData.badge && {badge: rankedCnKData.badge}),
                ...(rankedCnKData.progressBar && {progressBar: rankedCnKData.progressBar}),
            },
        ]

        const rushAvailableInSeason = this.rankedStates.some((state) => state.eloType === ELOTypeEnum.VisibleRanked4PRush)
        if(rushAvailableInSeason && ClientUserState.abTests?.RANKED_RUSH_4P === 'VISIBLE_RANKED_RUSH_4P') {
            cards.push({
                card: {
                    icon: LobbyImage.GameModeColonistRush,
                    category: MatchmakingCardCategory.Ranked,
                    title: {key: 'strings:homePage.matchmakingSelect.cards.rush'},
                    ...(rankedRush4PData.description && {subtitle: rankedRush4PData.description}),
                    seasonNote: {key: 'strings:homePage.matchmakingSelect.cards.rushSeasonNote'},
                    time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedRush4P),
                    playerCount: rankedRush4PCounts.playerCount,
                    botCount: rankedRush4PCounts.botCount,
                    matchType: MatchmakingMatchTypes.RankedRush4P,
                },
                ...(rankedRush4PData.badge && {badge: rankedRush4PData.badge}),
            })
        }

        this.cnKProgressText = rankedCnKData.badge?.text

        for(const {card, badge, progressBar} of cards) {
            this.generateCard(card, badge, progressBar)
        }
    }

    private generateCard(cardData: MatchmakingCard, badgeData?: BadgeData, progressBarData?: ProgressBarData) {
        const cardElement = createDivIn(this.cardsContainer, 'mm-mode-card hidden')

        createImgIn(cardElement, cardData.icon, 'mm-mode-card-icon')
        const content = createDivIn(cardElement, 'mm-mode-card-content')
        const header = createDivIn(content, 'mm-mode-card-header')
        createHeaderIn(header, cardData.title, 'mm-mode-card-title')
        if(cardData.subtitle) createParagraphIn(header, cardData.subtitle, 'mm-mode-card-subtitle')
        if(cardData.description) {
            const descriptionElement = createParagraphIn(header, cardData.description, 'mm-mode-card-description')
            if(cardData.showDescriptionLockIcon) descriptionElement.prepend(createImgIn(descriptionElement, LobbyImage.LockOn, 'mm-mode-card-description-lock-icon'))
        }
        if(cardData.seasonNote) createParagraphIn(header, cardData.seasonNote, 'mm-mode-card-season-note')

        const details = createDivIn(content, 'mm-mode-card-details')

        if(cardData.time != undefined) UIModeSelectorController.createOrUpdateTime(cardElement, details, cardData.time)

        UIModeSelectorController.createOrUpdatePlayerCount(cardElement, details, cardData.playerCount)
        UIModeSelectorController.createOrUpdateBotCount(cardElement, details, cardData.botCount)

        UIModeSelectorController.setCardEligibility(cardElement, cardData)

        setElementOnClick(cardElement, () => {
            this.selectCard(card, true)
        })

        if(cardData.nextRotationISO != undefined) {
            const timeUntilRotationEnd = TimeUtils.formatTimeLeft(new Date(cardData.nextRotationISO))
            UIModeSelectorController.createOrUpdateCountdown(cardElement, details, timeUntilRotationEnd)
        }

        let progressBarElement: UIProgressBarElement | undefined
        if(progressBarData != undefined) progressBarElement = new UIProgressBarElement(content, progressBarData)

        let badgeElement: UIProgressBadgeElement | undefined
        if(badgeData != undefined) {
            const badgeParent = badgeData.placement == 'bottom-row' ? content : cardElement
            badgeElement = new UIProgressBadgeElement(badgeParent, badgeData)
        }

        const infoIcon = createImgIn(cardElement, LobbyImage.InfoIcon, 'mm-mode-card-info-icon hidden')

        const card: ModeCard = {
            element: cardElement,
            data: cardData,
            ...(badgeElement != undefined && {badge: badgeElement}),
            ...(progressBarElement != undefined && {progressBar: progressBarElement}),
            infoIcon: infoIcon,
        }

        this.cards.push(card)
    }

    private static setCardEligibility(cardElement: HTMLDivElement, card: MatchmakingCard) {
        if(isBotMatchType(card.matchType)) return

        const matchType = card.matchType
        const {isEligible} = getMatchTypeEligibilityAndCreatePopup(matchType)

        cardElement.classList.toggle('disabled', !isEligible)
    }

    private selectCard(card: ModeCard, isPhysicalClick: boolean = false) {
        this.selectedCard?.element.classList.remove('selected')
        this.selectedCard?.infoIcon.classList.add('hidden')

        if(this.selectedCard == card && isPhysicalClick) this.onShowDetails(card.data.matchType)

        this.selectedCard = card
        this.selectedCard.element.classList.add('selected')
        this.selectedCard.infoIcon.classList.remove('hidden')

        updateMatchmakingPlayButtonState(this.playButton, card.data.matchType)
        this.saveCurrentSelection()
    }

    private handleTabClick(tab: MatchmakingCardCategory) {
        this.currentTab = tab

        this.botsTab.classList.toggle('selected', this.currentTab == MatchmakingCardCategory.Bots)
        this.casualTab.classList.toggle('selected', this.currentTab == MatchmakingCardCategory.Casual)
        this.rankedTab.classList.toggle('selected', this.currentTab == MatchmakingCardCategory.Ranked)
        this.selectedCard?.element.classList.remove('selected')

        this.setCardsView()
        this.selectFirstCardOfCurrentTab()
        this.saveCurrentSelection()
        this.updateModeSelectorAdVisibility()
    }

    private setCardsView() {
        for(const card of this.cards) {
            card.element.classList.toggle('hidden', card.data.category != this.currentTab)
        }
    }

    private selectFirstCardOfCurrentTab() {
        if(this.currentTab == undefined) this.selectTab(MatchmakingCardCategory.Bots)

        const firstCard = this.cards.find((card) => card.data.category == this.currentTab)

        if(firstCard == undefined) return
        this.selectCard(firstCard)
    }

    private selectLastPlayedMatchmakingMode() {
        const savedSelection = StorageWrapper.getLastSelectedMatchmakingMode()
        if(savedSelection == null) {
            this.selectFirstCardOfCurrentTab()
            return
        }

        this.handleTabClick(savedSelection.category)

        const lastSelectedCard = this.cards.find((card) => card.data.category == savedSelection.category && card.data.matchType == savedSelection.matchType)

        if(lastSelectedCard == undefined) {
            this.selectFirstCardOfCurrentTab()
            return
        }

        this.selectCard(lastSelectedCard)
    }

    private saveCurrentSelection() {
        const matchType = this.selectedCard?.data.matchType
        if(matchType == undefined) return
        if(this.currentTab == undefined) return

        StorageWrapper.setLastSelectedMatchmakingMode({
            category: this.currentTab,
            matchType: matchType,
        })
    }

    private handlePlayButtonClick() {
        const matchType = this.selectedCard?.data.matchType
        if(matchType == undefined) return

        if(isMatchTypeCasualLocked(matchType)) {
            const casualMatchType = getCasualRedirectMatchType(matchType)
            if(casualMatchType != undefined) this.selectCardByMatchType(casualMatchType)
            return
        }

        if(matchType == MatchmakingMatchTypes.RankedCitiesAndKnights4P) {
            const ckRankedState = getClientCKRankedState()
            if(ckRankedState == CKRankedState.CanRollDice) {
                new ViewPopupFreePlayDiceRoll()
                return
            }
        }

        const gameModeString = getGameModeStringForAnalytics(matchType)
        if(!this.hasTrackedFirstQueueStart) {
            trackMatchmakingTimeToQueueStart(this.componentShownTimestamp, gameModeString)
            this.hasTrackedFirstQueueStart = true
        }

        this.onStartGame(matchType)
    }

    private resetRankedCards() {
        const rankedCards = this.cards.filter((card) => card.data.category == MatchmakingCardCategory.Ranked)

        const ranked4PData = getMatchTypeDetails(MatchmakingMatchTypes.RankedClassic4P, this.rankedStates)
        const ranked1v1Data = getMatchTypeDetails(MatchmakingMatchTypes.RankedClassic1v1, this.rankedStates)
        const rankedCnKData = getMatchTypeDetails(MatchmakingMatchTypes.RankedCitiesAndKnights4P, this.rankedStates)
        const rankedRush4PData = getMatchTypeDetails(MatchmakingMatchTypes.RankedRush4P, this.rankedStates)

        rankedCards.forEach((card) => {
            let rankedData: RankedMatchTypeData
            switch(card.data.matchType) {
                case MatchmakingMatchTypes.RankedClassic4P:
                    rankedData = ranked4PData
                    break
                case MatchmakingMatchTypes.RankedClassic1v1:
                    rankedData = ranked1v1Data
                    break
                case MatchmakingMatchTypes.RankedCitiesAndKnights4P:
                    rankedData = rankedCnKData
                    break
                case MatchmakingMatchTypes.RankedRush4P:
                    rankedData = rankedRush4PData
                    break
                default:
                    return
            }

            if(rankedData.subtitle != undefined) {
                card.data.subtitle = rankedData.subtitle
                UIModeSelectorController.createOrUpdateSubtitle(card.element, rankedData.subtitle)
            }

            if(rankedData.description) {
                card.data.description = rankedData.description
                card.data.showDescriptionLockIcon = rankedData.showDescriptionLockIcon
                UIModeSelectorController.createOrUpdateDescription(card.element, rankedData.description, rankedData.showDescriptionLockIcon)
            }

            if(rankedData.progressBar) UIModeSelectorController.updateCardProgressBar(card, rankedData.progressBar)

            if(rankedData.badge) {
                UIModeSelectorController.updateCardBadge(card, rankedData.badge)

                if(card.data.matchType == MatchmakingMatchTypes.RankedCitiesAndKnights4P) {
                    this.cnKProgressText = rankedData.badge.text
                    this.startCountdownTimer()
                }
            }

            UIModeSelectorController.setCardEligibility(card.element, card.data)
        })
    }

    private startCountdownTimer() {
        if(this.countdownInterval) clearInterval(this.countdownInterval)

        const doesUserHaveFreePlayFromDiceRoll = ClientUserState.userState?.ckFreeGameUntil != undefined && ClientUserState.userState?.ckFreeGameUntil.getTime() > Date.now()
        this.cnKCountdownEndTimestamp = doesUserHaveFreePlayFromDiceRoll ? ClientUserState.userState?.ckFreeGameUntil?.getTime() : ClientUserState.userState?.ckNextRerollAt?.getTime()

        const shouldUpdateCnK = this.shouldUpdateCnKCardCountdownTimer()
        const shouldUpdateShuffle = this.shouldUpdateShuffleCardCountdownTimer()

        if(!shouldUpdateCnK && !shouldUpdateShuffle) return

        this.countdownInterval = setInterval(() => {
            if(shouldUpdateCnK) this.updateCnKCardCountdownTimer()
            if(shouldUpdateShuffle) this.updateShuffleCardCountdownTimer()
        }, 1000)
    }

    private updateCnKCardCountdownTimer() {
        const rankedCnKCard = this.cards.filter((card) => card.data.category == MatchmakingCardCategory.Ranked).filter((card) => card.data.matchType == MatchmakingMatchTypes.RankedCitiesAndKnights4P)
        if(rankedCnKCard.length == 0) return

        if(this.cnKCountdownEndTimestamp == undefined) return

        if(this.cnKCountdownEndTimestamp < Date.now()) {
            const cnkRankedData = getMatchTypeDetails(MatchmakingMatchTypes.RankedCitiesAndKnights4P, this.rankedStates)
            this.cnKProgressText = cnkRankedData.badge?.text
            this.resetRankedCards()
        }

        if(this.cnKProgressText == undefined) return
        const timeUntilCountdownEnd = TimeUtils.formatCountdownString(new Date(this.cnKCountdownEndTimestamp))

        const progressElement = rankedCnKCard[0].element.querySelector('.mm-mode-card-progress p, .mm-mode-card-status-badge p') as HTMLParagraphElement | null
        if(progressElement) setElementInnerText(progressElement, {key: this.cnKProgressText.key, options: {time: timeUntilCountdownEnd}})
    }

    private shouldUpdateCnKCardCountdownTimer(): boolean {
        if(this.cnKCountdownEndTimestamp == undefined) return false
        if(this.cnKCountdownEndTimestamp < Date.now()) return false

        if(this.cnKProgressText == undefined) return false
        if(this.cnKProgressText.key == 'strings:homePage.matchmakingSelect.freeGameFromDiceRoll') return true
        if(this.cnKProgressText.key == 'strings:homePage.matchmakingSelect.dailyFreeRollWaiting') return true

        return false
    }

    private updateShuffleCardCountdownTimer() {
        const shuffleCard = this.cards.find((card) => card.data.category == MatchmakingCardCategory.Casual && card.data.matchType == MatchmakingMatchTypes.UnrankedShuffle)
        if(shuffleCard == undefined) return
        if(shuffleCard.data.nextRotationISO == undefined) return

        const rotationEndTimestamp = new Date(shuffleCard.data.nextRotationISO).getTime()
        const detailsContainer = shuffleCard.element.querySelector('.mm-mode-card-details') as HTMLDivElement

        if(rotationEndTimestamp < Date.now()) {
            UIModeSelectorController.createOrUpdateCountdown(shuffleCard.element, detailsContainer, {key: 'strings:homePage.matchmakingSelect.newQueueSoon'})
            return
        }

        const timeUntilRotationEnd = TimeUtils.formatTimeLeft(new Date(rotationEndTimestamp))
        UIModeSelectorController.createOrUpdateCountdown(shuffleCard.element, detailsContainer, timeUntilRotationEnd)
    }

    private shouldUpdateShuffleCardCountdownTimer(): boolean {
        const shuffleCard = this.cards.find((card) => card.data.category == MatchmakingCardCategory.Casual && card.data.matchType == MatchmakingMatchTypes.UnrankedShuffle)
        if(shuffleCard == undefined) return false
        if(shuffleCard.data.nextRotationISO == undefined) return false

        const rotationEndTimestamp = new Date(shuffleCard.data.nextRotationISO).getTime()
        if(rotationEndTimestamp < Date.now()) return false

        return true
    }

    private updateShuffleCard() {
        const shuffleCard = this.cards.find((card) => card.data.category == MatchmakingCardCategory.Casual && card.data.matchType == MatchmakingMatchTypes.UnrankedShuffle)
        const shuffleQueueData = ClientShuffleQueueController.shuffleQueueData
        if(shuffleCard == undefined || shuffleQueueData == undefined) return

        const shuffleDetails = getMatchTypeDetails(MatchmakingMatchTypes.UnrankedShuffle)

        const hasPlayedEnoughGames = (ClientUserState.userState?.totalCompletedGameCount ?? 0) >= RankedConstants.NumberOfGamesToUnlockRanked
        const iconElement = shuffleCard.element.querySelector('.mm-mode-card-icon')
        if(iconElement && hasPlayedEnoughGames) {
            const stackedIcon = createShuffleQueueIconStack(shuffleQueueData.mapId, shuffleQueueData.modeId)
            if(stackedIcon) {
                stackedIcon.classList.add('mm-mode-card-icon')
                iconElement.replaceWith(stackedIcon)
            }
        }

        const detailsContainer = shuffleCard.element.querySelector('.mm-mode-card-details') as HTMLDivElement

        shuffleCard.data.time = getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedShuffle)
        if(shuffleCard.data.time != undefined) UIModeSelectorController.createOrUpdateTime(shuffleCard.element, detailsContainer, shuffleCard.data.time)

        const playerAndBotCount = getMatchTypePlayerAndBotCount(MatchmakingMatchTypes.UnrankedShuffle)
        shuffleCard.data.playerCount = playerAndBotCount.playerCount
        UIModeSelectorController.createOrUpdatePlayerCount(shuffleCard.element, detailsContainer, playerAndBotCount.playerCount)
        shuffleCard.data.botCount = playerAndBotCount.botCount
        UIModeSelectorController.createOrUpdateBotCount(shuffleCard.element, detailsContainer, playerAndBotCount.botCount)

        if(shuffleDetails.subtitle != undefined) {
            shuffleCard.data.subtitle = shuffleDetails.subtitle
            UIModeSelectorController.createOrUpdateSubtitle(shuffleCard.element, shuffleDetails.subtitle)
        }

        if(shuffleDetails.description) {
            shuffleCard.data.description = shuffleDetails.description
            shuffleCard.data.showDescriptionLockIcon = shuffleDetails.showDescriptionLockIcon
            UIModeSelectorController.createOrUpdateDescription(shuffleCard.element, shuffleDetails.description, shuffleDetails.showDescriptionLockIcon)
        }

        if(shuffleDetails.progressBar) UIModeSelectorController.updateCardProgressBar(shuffleCard, shuffleDetails.progressBar)

        if(shuffleDetails.badge) UIModeSelectorController.updateCardBadge(shuffleCard, shuffleDetails.badge)

        if(shuffleQueueData.endDate) {
            shuffleCard.data.nextRotationISO = shuffleQueueData.endDate

            const timeUntilRotationEnd = TimeUtils.formatTimeLeft(new Date(shuffleQueueData.endDate))
            UIModeSelectorController.createOrUpdateCountdown(shuffleCard.element, detailsContainer, timeUntilRotationEnd)

            this.startCountdownTimer()
        }

        if(this.selectedCard == shuffleCard) updateMatchmakingPlayButtonState(this.playButton, shuffleCard.data.matchType)

        UIModeSelectorController.setCardEligibility(shuffleCard.element, shuffleCard.data)
        UIModeSelectorController.addNewBadgeIfNeeded(shuffleCard.element)
    }

    private static updateCardBadge(card: ModeCard, badgeData: BadgeData) {
        const badgeParent = badgeData.placement == 'bottom-row'
            ? card.element.querySelector('.mm-mode-card-content') as HTMLDivElement
            : card.element
        if(card.badge == undefined) {
            card.badge = new UIProgressBadgeElement(badgeParent, badgeData)
            return
        }
        card.badge.update(badgeData)
    }

    private static updateCardProgressBar(card: ModeCard, progressBarData: ProgressBarData) {
        const content = card.element.querySelector('.mm-mode-card-content') as HTMLDivElement
        if(card.progressBar == undefined) {
            card.progressBar = new UIProgressBarElement(content, progressBarData)
            return
        }
        card.progressBar.update(progressBarData)
    }

    private static createOrUpdateTime(cardElement: HTMLElement, detailsContainer: HTMLElement, time: number) {
        const existingElement = cardElement.querySelector('.mm-mode-card-time p') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: time.toString()}})
            return
        }

        const timeDiv = createDivIn(detailsContainer, 'mm-mode-card-detail mm-mode-card-time')
        createExternalImgIn(timeDiv, IconPlayTime, {key: 'strings:colonistCommon.utils.#buffer', options: {value: time.toString()}})
        createParagraphIn(timeDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: time.toString()}}, '')
    }

    private static createOrUpdatePlayerCount(cardElement: HTMLElement, detailsContainer: HTMLElement, playerCount: number) {
        const existingElement = cardElement.querySelector('.mm-mode-card-player-count p') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: playerCount.toString()}})
            return
        }

        const playerCountDiv = createDivIn(detailsContainer, 'mm-mode-card-detail mm-mode-card-player-count')
        createExternalImgIn(playerCountDiv, IconPlayerCount, {key: 'strings:colonistCommon.utils.#buffer', options: {value: playerCount.toString()}})
        createParagraphIn(playerCountDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: playerCount.toString()}}, '')
    }

    private static createOrUpdateBotCount(cardElement: HTMLElement, detailsContainer: HTMLElement, botCount: number) {
        const isHidden = botCount <= 0

        const existingElement = cardElement.querySelector('.mm-mode-card-bot-count p') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: botCount.toString()}})
            existingElement.parentElement?.classList.toggle('hidden', isHidden)
            return
        }

        const botCountDiv = createDivIn(detailsContainer, 'mm-mode-card-detail mm-mode-card-bot-count')
        createExternalImgIn(botCountDiv, IconBotCount, {key: 'strings:colonistCommon.utils.#buffer', options: {value: botCount.toString()}})
        createParagraphIn(botCountDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: botCount.toString()}}, '')
        botCountDiv.classList.toggle('hidden', isHidden)
    }

    private static createOrUpdateCountdown(cardElement: HTMLElement, detailsContainer: HTMLElement, countdownText: TranslationObject) {
        const existingElement = cardElement.querySelector('.mm-mode-card-countdown p') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, countdownText)
            return
        }

        const countdown = createDivIn(detailsContainer, 'mm-mode-card-detail mm-mode-card-countdown')
        createImgIn(countdown, LobbyImage.IconRotation, 'mm-mode-card-detail-icon')
        createParagraphIn(countdown, countdownText, '')
    }

    private static createOrUpdateSubtitle(cardElement: HTMLElement, subtitle: TranslationObject) {
        const existingElement = cardElement.querySelector('.mm-mode-card-subtitle') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, subtitle)
            return
        }

        const header = cardElement.querySelector('.mm-mode-card-header') as HTMLDivElement
        const subtitleElement = createParagraphIn(header, subtitle, 'mm-mode-card-subtitle')
        const descriptionElement = cardElement.querySelector('.mm-mode-card-description')
        if(descriptionElement) header.insertBefore(subtitleElement, descriptionElement)
    }

    private static createOrUpdateDescription(cardElement: HTMLElement, description: TranslationObject, showLockIcon?: boolean) {
        const existingElement = cardElement.querySelector('.mm-mode-card-description') as HTMLParagraphElement | null
        if(existingElement) {
            setElementInnerText(existingElement, description)
            if(showLockIcon) existingElement.prepend(createImgIn(existingElement, LobbyImage.LockOn, 'mm-mode-card-description-lock-icon'))
            return
        }

        const header = cardElement.querySelector('.mm-mode-card-header') as HTMLDivElement
        const descriptionElement = createParagraphIn(header, description, 'mm-mode-card-description')
        if(showLockIcon) descriptionElement.prepend(createImgIn(descriptionElement, LobbyImage.LockOn, 'mm-mode-card-description-lock-icon'))
    }
}
